/**
 * Profile Page Component
 *
 * Displays and allows editing of user profile information.
 */

import { useState, useEffect } from 'react'
import { useAuth } from '@/features/auth/AuthContext'
import { supabase } from '@/lib/supabaseClient'
import {
  getMyPartnership,
  createPartnershipInvitation,
  acceptPartnershipInvitation,
  declinePartnershipInvitation,
  removePartnership,
  checkPendingInvitations,
  claimPendingInvitation
} from '@/features/partnerships/api/partnershipsApi'

export default function ProfilePage() {
  const { user, profile, refreshProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [formData, setFormData] = useState({
    full_name: '',
    countryCode: '+64',
    mobileNumber: '',
    company_name: '',
    website: '',
    bio: '',
    address: ''
  })

  const [partnership, setPartnership] = useState(null)
  const [pendingInvite, setPendingInvite] = useState(null)
  const [partnershipLoading, setPartnershipLoading] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [partnershipError, setPartnershipError] = useState(null)
  const [partnershipSuccess, setPartnershipSuccess] = useState(null)

  const countryCodes = [
    { code: '+64', country: 'New Zealand', keywords: ['new zealand', 'nz', 'auckland', 'wellington', 'christchurch'] },
    { code: '+61', country: 'Australia', keywords: ['australia', 'au', 'sydney', 'melbourne', 'brisbane'] },
    { code: '+1', country: 'USA/Canada', keywords: ['usa', 'united states', 'america', 'canada', 'us'] },
    { code: '+44', country: 'UK', keywords: ['uk', 'united kingdom', 'britain', 'england', 'london'] },
    { code: '+65', country: 'Singapore', keywords: ['singapore'] },
    { code: '+852', country: 'Hong Kong', keywords: ['hong kong', 'hk'] },
    { code: '+86', country: 'China', keywords: ['china', 'beijing', 'shanghai'] },
    { code: '+91', country: 'India', keywords: ['india', 'mumbai', 'delhi'] },
    { code: '+81', country: 'Japan', keywords: ['japan', 'tokyo'] },
    { code: '+82', country: 'South Korea', keywords: ['korea', 'seoul'] },
  ]

  const detectCountryFromAddress = (address) => {
    if (!address) return '+64'
    const lowerAddress = address.toLowerCase()
    for (const country of countryCodes) {
      if (country.keywords.some(keyword => lowerAddress.includes(keyword))) {
        return country.code
      }
    }
    return '+64'
  }

  const parseExistingPhone = (phone) => {
    if (!phone) return { countryCode: '+64', mobileNumber: '' }
    const match = phone.match(/^(\+\d{1,4})\s*(.*)$/)
    if (match) {
      const foundCode = countryCodes.find(c => c.code === match[1])
      return {
        countryCode: foundCode ? match[1] : '+64',
        mobileNumber: match[2] || ''
      }
    }
    return { countryCode: '+64', mobileNumber: phone }
  }

  useEffect(() => {
    if (profile) {
      const { countryCode, mobileNumber } = parseExistingPhone(profile.phone)
      setFormData({
        full_name: profile.full_name || '',
        countryCode: countryCode,
        mobileNumber: mobileNumber,
        company_name: profile.company_name || '',
        website: profile.website || '',
        bio: profile.bio || '',
        address: profile.address || ''
      })

      if (profile.role === 'buyer') {
        loadPartnership()
      }
    }
  }, [profile])

  async function loadPartnership() {
    try {
      setPartnershipLoading(true)
      setPartnershipError(null)

      const pending = await checkPendingInvitations()
      if (pending) {
        setPendingInvite(pending)
      }

      const data = await getMyPartnership()
      setPartnership(data)
    } catch (err) {
      console.error('Error loading partnership:', err)
      setPartnershipError(err.message)
    } finally {
      setPartnershipLoading(false)
    }
  }

  async function handleInvite(e) {
    e.preventDefault()
    setIsSubmitting(true)
    setPartnershipError(null)
    setPartnershipSuccess(null)

    try {
      await createPartnershipInvitation(inviteEmail)
      setPartnershipSuccess('Link invitation sent!')
      setInviteEmail('')
      await loadPartnership()
    } catch (err) {
      setPartnershipError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleAccept(partnershipId) {
    setIsSubmitting(true)
    setPartnershipError(null)

    try {
      await acceptPartnershipInvitation(partnershipId)
      setPartnershipSuccess('Successfully linked!')
      await loadPartnership()
    } catch (err) {
      setPartnershipError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDecline(partnershipId) {
    if (!confirm('Are you sure you want to decline this link invitation?')) {
      return
    }

    setIsSubmitting(true)
    setPartnershipError(null)

    try {
      await declinePartnershipInvitation(partnershipId)
      setPartnershipSuccess('Invitation declined')
      await loadPartnership()
    } catch (err) {
      setPartnershipError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleRemove(partnershipId) {
    if (!confirm('Are you sure you want to unlink your partner? This will stop sharing your following lists.')) {
      return
    }

    setIsSubmitting(true)
    setPartnershipError(null)

    try {
      await removePartnership(partnershipId)
      setPartnershipSuccess('Successfully unlinked')
      setPartnership(null)
    } catch (err) {
      setPartnershipError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleClaimInvite() {
    setIsSubmitting(true)
    setPartnershipError(null)

    try {
      await claimPendingInvitation()
      setPartnershipSuccess('Invitation claimed! You can now accept or decline.')
      setPendingInvite(null)
      await loadPartnership()
    } catch (err) {
      setPartnershipError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)

    try {
      const fullPhone = formData.mobileNumber
        ? `${formData.countryCode} ${formData.mobileNumber}`.trim()
        : ''

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.full_name,
          phone: fullPhone,
          company_name: formData.company_name,
          website: formData.website,
          bio: formData.bio,
          address: formData.address,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)

      if (error) throw error

      await refreshProfile()
      setMessage({ type: 'success', text: 'Profile updated successfully' })
    } catch (error) {
      console.error('Error updating profile:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-secondary-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900">Profile</h1>
          <p className="mt-2 text-secondary-600">
            Manage your personal information and preferences
          </p>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 overflow-hidden">
          <div className="p-6 border-b border-secondary-200">
            <h2 className="text-lg font-semibold text-secondary-900">Personal Information</h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Message */}
            {message && (
              <div
                className={`p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {message.text}
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="input bg-secondary-50 cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-secondary-500">
                Email cannot be changed
              </p>
            </div>

            {/* Full Name */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-secondary-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="input"
              />
            </div>

            {/* Address Section */}
            {profile?.role === 'buyer' && (
              <div className="pt-4 border-t border-secondary-200">
                <div className="mb-4">
                  <h3 className="text-md font-medium text-secondary-900">Address</h3>
                  <p className="text-sm text-secondary-500">
                    Required for proceeding to Sale and Purchase Agreement
                  </p>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-secondary-700 mb-2">
                    Full Address *
                  </label>
                  {profile?.address ? (
                    <div className="space-y-3">
                      <div className="p-4 bg-secondary-50 border border-secondary-200 rounded-lg">
                        <p className="text-secondary-900">{profile.address}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, address: '' }))}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Change address
                      </button>
                    </div>
                  ) : (
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={(e) => {
                        handleChange(e)
                        const detectedCode = detectCountryFromAddress(e.target.value)
                        if (!formData.mobileNumber) {
                          setFormData(prev => ({ ...prev, countryCode: detectedCode }))
                        }
                      }}
                      rows={3}
                      className="input"
                      placeholder="Enter your full address"
                      required={profile?.role === 'buyer'}
                    />
                  )}
                </div>

                <div className="mt-6">
                  <label htmlFor="mobileNumber" className="block text-sm font-medium text-secondary-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="flex gap-2">
                    <select
                      id="countryCode"
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="input w-36"
                    >
                      {countryCodes.map(({ code, country }) => (
                        <option key={code} value={code}>
                          {code} ({country})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="mobileNumber"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      className="input flex-1"
                      placeholder="21 123 4567"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Company Name (for developers) */}
            {profile?.role === 'developer' && (
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-secondary-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="input"
                  placeholder="Your Company Inc."
                />
              </div>
            )}

            {/* Website (for developers) */}
            {profile?.role === 'developer' && (
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-secondary-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="input"
                  placeholder="https://example.com"
                />
              </div>
            )}

            {/* Bio (for developers) */}
            {profile?.role === 'developer' && (
              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-secondary-700 mb-2">
                  Bio
                </label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  className="input"
                  placeholder="Tell buyers about your company and experience..."
                />
              </div>
            )}

            {/* Role (read-only) */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Account Type
              </label>
              <div className="px-4 py-2 bg-secondary-50 border border-secondary-200 rounded-lg">
                <span className="capitalize text-secondary-900">{profile?.role}</span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-4 border-t border-secondary-200">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary min-w-32"
              >
                {saving ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner spinner-sm mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Partner Linking - Only for buyers */}
        {profile?.role === 'buyer' && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-secondary-200 overflow-hidden">
            <div className="p-6 border-b border-secondary-200">
              <h2 className="text-lg font-semibold text-secondary-900">Partner Linking</h2>
              <p className="mt-1 text-sm text-secondary-600">
                Connect with your partner to share and view each other's followed projects. When you both follow the same project, you'll see it highlighted on your dashboard.
              </p>
            </div>
            <div className="p-6">
              {partnershipLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner spinner-md text-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {partnershipError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      {partnershipError}
                    </div>
                  )}

                  {partnershipSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                      {partnershipSuccess}
                    </div>
                  )}

                  {pendingInvite && !partnership && (
                    <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
                      <p className="text-sm text-secondary-700 mb-3">
                        <strong>{pendingInvite.initiator?.full_name || pendingInvite.initiator?.email}</strong> wants to link with you
                      </p>
                      <button
                        onClick={handleClaimInvite}
                        disabled={isSubmitting}
                        className="btn-primary text-sm"
                      >
                        {isSubmitting ? 'Processing...' : 'Claim Invitation'}
                      </button>
                    </div>
                  )}

                  {partnership?.status === 'pending' && !partnership.is_initiator && (
                    <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
                      <p className="text-sm text-secondary-700 mb-3">
                        <strong>{partnership.partner_name || partnership.partner_email}</strong> wants to link with you
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAccept(partnership.id)}
                          disabled={isSubmitting}
                          className="btn-primary text-sm"
                        >
                          {isSubmitting ? 'Processing...' : 'Accept'}
                        </button>
                        <button
                          onClick={() => handleDecline(partnership.id)}
                          disabled={isSubmitting}
                          className="px-4 py-2 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 text-sm"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  )}

                  {partnership?.status === 'pending' && partnership.is_initiator && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-secondary-700 mb-2">
                        Waiting for <strong>{partnership.partner_name || partnership.partner_email}</strong> to accept your invitation
                      </p>
                      <button
                        onClick={() => handleRemove(partnership.id)}
                        disabled={isSubmitting}
                        className="text-sm text-error-600 hover:text-error-700 underline"
                      >
                        {isSubmitting ? 'Processing...' : 'Cancel Invitation'}
                      </button>
                    </div>
                  )}

                  {partnership?.status === 'accepted' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <p className="text-sm font-semibold text-green-900">
                              Linked with {partnership.partner_name || partnership.partner_email}
                            </p>
                          </div>
                          <p className="text-xs text-green-700">
                            You can now see each other's followed projects in your dashboard
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemove(partnership.id)}
                          disabled={isSubmitting}
                          className="text-sm text-error-600 hover:text-error-700 underline flex-shrink-0 ml-4"
                        >
                          {isSubmitting ? 'Processing...' : 'Unlink'}
                        </button>
                      </div>
                    </div>
                  )}

                  {!partnership && !pendingInvite && (
                    <div>
                      <p className="text-sm text-secondary-600 mb-4">
                        Invite your partner to link accounts and share your followed projects together.
                      </p>
                      <form onSubmit={handleInvite} className="space-y-3">
                        <div>
                          <label htmlFor="partner_email" className="block text-sm font-medium text-secondary-700 mb-2">
                            Partner's Email
                          </label>
                          <input
                            type="email"
                            id="partner_email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="partner@email.com"
                            className="input"
                            required
                            disabled={isSubmitting}
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={isSubmitting || !inviteEmail}
                          className="btn-primary w-full"
                        >
                          {isSubmitting ? 'Sending...' : 'Send Link Invitation'}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Security */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-secondary-200 overflow-hidden">
          <div className="p-6 border-b border-secondary-200">
            <h2 className="text-lg font-semibold text-secondary-900">Security</h2>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-secondary-100">
              <div>
                <p className="text-sm font-medium text-secondary-700">Account Created</p>
                <p className="text-sm text-secondary-500">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b border-secondary-100">
              <div>
                <p className="text-sm font-medium text-secondary-700">Last Updated</p>
                <p className="text-sm text-secondary-500">
                  {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center py-3">
              <div>
                <p className="text-sm font-medium text-secondary-700">User ID</p>
                <p className="text-sm text-secondary-500 font-mono">{user?.id}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
